import { useEffect, useMemo, useState } from "react";
import { ListingCard } from "../components/ListingCard";
import { ListingFilters } from "../components/marketplace/ListingFilters";
import { ListingSearchBar } from "../components/marketplace/ListingSearchBar";
import { MetricCard } from "../components/MetricCard";
import { Button } from "../components/ui/Button";
import { useMarketplaceFilters } from "../hooks/useMarketplaceFilters";
import { marketplaceService } from "../services/marketplaceService";
import type { Listing } from "../types/domain";

export function MarketplacePage() {
  const { filters, listingFilters, dispatch } = useMarketplaceFilters();
  const [listings, setListings] = useState<Listing[]>(() => marketplaceService.listListings(listingFilters));
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    marketplaceService.refreshListings(listingFilters)
      .then(nextListings => {
        if (!cancelled) {
          setListings(nextListings);
          setError(null);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [listingFilters]);

  const pharmacies = marketplaceService.listPharmacies();
  const sellerName = (id: string) => pharmacies.find(pharmacy => pharmacy.id === id)?.name ?? "Verified pharmacy";
  const activeInventory = useMemo(() => listings.reduce((sum, listing) => sum + listing.unitPrice * listing.quantity, 0), [listings]);

  return (
    <div className="space-y-5">
      <header className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm ">
        <p className="text-xs font-black uppercase tracking-[.28em] text-slate-500">Marketplace</p>
        <div className="mt-3 flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h1 className="text-4xl font-black">Browse verified pharmaceutical inventory</h1>
            <p className="mt-3 max-w-3xl text-slate-600">Search live active pharmacy-to-pharmacy listings by NDC, product, lot, or operational constraints.</p>
          </div>
          <ListingSearchBar query={filters.query} onQueryChange={query => dispatch({ type: "setQuery", query })} />
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-4">
          <MetricCard label="marketplace members" value={String(Math.max(pharmacies.length, 1))} />
          <MetricCard label="active inventory" value={`$${activeInventory.toLocaleString()}`} />
          <MetricCard label="NDC listings" value={String(listings.length)} />
          <MetricCard label="data source" value="API" />
        </div>
      </header>
      <ListingFilters filters={filters} resultCount={listings.length} dispatch={dispatch} />
      {isLoading ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-600 ">
          <h2 className="text-2xl font-black text-slate-950">Loading live listings…</h2>
          <p className="mt-2">Fetching from MatchRx API.</p>
        </section>
      ) : error ? (
        <section className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-slate-700 ">
          <h2 className="text-2xl font-black text-slate-950">Sign in required for live marketplace data.</h2>
          <p className="mt-2">{error}</p>
          <Button className="mt-5" href="/auth/login">Sign in</Button>
        </section>
      ) : listings.length > 0 ? (
        listings.map(listing => <ListingCard key={listing.id} listing={listing} sellerName={sellerName(listing.sellerId)} />)
      ) : (
        <section className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-600 ">
          <h2 className="text-2xl font-black text-slate-950">No listings match those filters.</h2>
          <p className="mt-2">Reset filters or search a broader NDC/product family.</p>
        </section>
      )}
    </div>
  );
}
